// ============================================================================
// LA NOTIFICATION DE L'IMPORT — en bas à droite, tant qu'un import tourne ou qu'on n'a pas lu son résultat (maquette).
//
// UTILITÉ : l'assistant se ferme au lancement ; c'est ici que l'opérateur apprend ce que le magasin a fait de son
// fichier, sur n'importe quel écran du back-office — et qu'il ouvre le rapport complet (lignes refusées, codes-barres
// internes attribués, document créé) ou le télécharge.
//
// ⚠️ « EN COURS » A UNE BARRE QUI DÉFILE, SANS POURCENTAGE : le serveur écrit l'import d'un seul geste et ne publie
//    aucune progression ; un pourcentage serait un chronomètre (§23.0). L'indicateur tourne et la barre défile tant que
//    la requête est en vol, rien de plus. ⚠️ AUCUNE DURÉE MINIMALE : un import rapide passe en quelques dixièmes de
//    seconde à « terminé » — afficher « en cours » au-delà annoncerait un travail déjà fini.
// ⚠️ UN REFUS DIT « RIEN N'A ÉTÉ ÉCRIT » : l'import vit dans une seule transaction, c'est vrai par construction.
// ============================================================================
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon, Modal, Trans } from '@orgazone/ui-web';
import type { ImportArticlesReport } from '../api';
import { key, t } from '../i18n';
import { dismissImportJob, requestShowImported, retryImport, useImportJob, type ImportScreenRejected } from './import-job';
import { fieldLabel } from '../vocabulary';
import { Tuile } from './article-import';

/** Le libellé de la destination — le même que la carte choisie à l'étape 4. */
const DESTINATION_LABEL = {
  bl: key('Bon de livraison'), cmd: key('Commande fournisseur'), stock: key('Entrée directe en stock'), ref: key('Articles seulement'),
} as const;

/**
 * LE RAPPORT EN CSV — les lignes refusées et les codes-barres attribués, à garder ou à corriger dans le tableur.
 * ⚠️ LES MOTS DES CHAMPS SONT CEUX DU MAGASIN (`fieldLabel`), nos en-têtes suivent la langue du lecteur, les valeurs jamais : un numéro de ligne ou un code-barres ne se traduit
 *    pas, et c'est lui qu'on recherche dans le fichier d'origine.
 */
export function importReportCsv(report: ImportArticlesReport, screenRejected: readonly ImportScreenRejected[] = []): string {
  const cell = (v: string | number) => {
    // ⚠️ UNE CELLULE QUI COMMENCE PAR « = + - @ » EST UNE FORMULE pour un tableur : une désignation ou un motif venus
    //    d'un fichier fournisseur s'exécuteraient à l'ouverture. Préfixée d'une apostrophe, elle reste du texte.
    const brut = String(v);
    const s = typeof v === 'string' && /^[=+\-@\t\r]/.test(brut) ? `'${brut}` : brut;
    return /[;"\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines: string[] = [];
  lines.push([t('Ligne'), fieldLabel('gencode'), t('Motif du refus')].map(cell).join(';'));
  // ⚠️ LES LIGNES ÉCARTÉES PAR L'ÉCRAN Y SONT AUSSI : elles ne sont jamais parties, et c'est ce rapport qu'on garde.
  for (const r of screenRejected) lines.push([r.line, '', r.ex && r.ex !== '—' ? `${r.why} — ${r.ex}` : r.why].map(cell).join(';'));
  for (const r of report.refused) lines.push([r.line, r.gencode ?? '', r.reason].map(cell).join(';'));
  lines.push('');
  lines.push([t('Ligne'), t('Code-barres interne attribué'), fieldLabel('name')].map(cell).join(';'));
  for (const g of report.generatedGencodes) lines.push([g.line, g.gencode, g.name].map(cell).join(';'));
  return lines.join('\r\n');
}

/** Télécharge le rapport — un fichier local, rien n'est renvoyé au serveur. */
function telecharger(report: ImportArticlesReport, fileName: string, screenRejected: readonly ImportScreenRejected[]): void {
  // ⚠️ L'EN-TÊTE UTF-8 (BOM) EN TÊTE DU FICHIER : sans lui, Excel lit le CSV en ANSI et casse les accents des motifs.
  const blob = new Blob([String.fromCharCode(0xfeff) + importReportCsv(report, screenRejected)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `rapport-import-${fileName.replace(/\.[^.]+$/, '')}.csv`;
  a.click();
  // ⚠️ LIBÉRÉE APRÈS COUP, pas dans la même seconde : certains navigateurs lisent l'adresse APRÈS le clic, et une
  //    adresse déjà libérée donne un téléchargement vide.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** La notification de l'import, montée UNE fois dans la coquille — elle survit à la fermeture de l'assistant. */
export function ImportJobNotice() {
  const job = useImportJob();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [rapport, setRapport] = useState(false);
  if (!job) return null;
  const done = job.state === 'done' ? job.report : null;
  const ecartees = done ? done.refused.length + job.screenRejected.length : 0;
  const rienEcrit = done !== null && done.written === 0 && done.skippedExisting === 0 && done.destination === null;
  return (
    <>
      <div role="status" aria-live="polite" style={{
        position: 'fixed', right: 20, bottom: 20, zIndex: 1200, width: 352, maxWidth: 'calc(100vw - 40px)', background: 'var(--paper)',
        border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', boxShadow: '0 20px 48px -12px rgba(12,14,16,.34)', overflow: 'hidden',
        animation: 'pop .26s cubic-bezier(.2,.7,.3,1)',
      }}>
        {job.state === 'running' && (
          <div style={{ padding: '16px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
              <span className="oz-spin" style={{ width: 32, height: 32, borderRadius: 'var(--r-ctrl)', background: 'var(--blue-bg)', color: 'var(--blue-fg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon name="spinner" size={17} stroke={2} />
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Import en cours')}</span>
                <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{job.fileName}</span>
              </span>
            </div>
            <div style={{ height: 6, borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', marginTop: 13, overflow: 'hidden' }}>
              <div data-testid="import-en-vol" className="oz-sweep" style={{ height: '100%', width: '40%', borderRadius: 'var(--r-pill)', background: 'var(--blue)' }} />
            </div>
            <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 10, lineHeight: 1.5 }}>
              {t('{n} ligne à traiter. Vous pouvez continuer à travailler — nous vous prévenons à la fin.', { n: job.lines })}
            </div>
          </div>
        )}
        {job.state === 'failed' && (
          <div>
            <div style={{ padding: '16px 18px', display: 'flex', alignItems: 'flex-start', gap: 11 }}>
              <span style={{ width: 32, height: 32, borderRadius: 'var(--r-ctrl)', background: job.certain ? 'var(--rose-bg)' : 'var(--amber-bg)', color: job.certain ? 'var(--rose-fg)' : 'var(--amber-fg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon name="alert" size={17} stroke={2.2} />
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                {/* ⚠️ « RIEN N'A ÉTÉ ÉCRIT » SEULEMENT QUAND LE MAGASIN L'A DIT : sans réponse, l'import a peut-être été écrit. */}
                <span style={{ display: 'block', fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)' }}>
                  {job.certain ? t('Import refusé — rien n’a été écrit') : t('Sans réponse du magasin — l’import a peut-être été écrit')}
                </span>
                <span role="alert" style={{ display: 'block', fontSize: 'var(--t-body)', color: 'var(--ink-600)', lineHeight: 1.5, marginTop: 3 }}>{job.reason}</span>
                {!job.certain && (
                  <span style={{ display: 'block', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', lineHeight: 1.5, marginTop: 4 }}>
                    {t('« Réessayer » renvoie le même import : s’il a déjà été écrit, rien ne s’écrit deux fois.')}
                  </span>
                )}
              </span>
              <FermerNotification />
            </div>
            {!job.certain && (
              <div style={{ display: 'flex', gap: 8, padding: '12px 18px', background: 'var(--ink-50)', borderTop: '1px solid var(--ink-200)' }}>
                <div style={{ flex: 1 }} />
                <button type="button" onClick={retryImport} style={{
                  height: 36, padding: '0 16px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--blue)',
                  color: '#fff', fontSize: 'var(--t-body)', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer',
                }}>{t('Réessayer')}</button>
              </div>
            )}
          </div>
        )}
        {done !== null && (
          <div>
            <div style={{ padding: '16px 18px', display: 'flex', alignItems: 'flex-start', gap: 11 }}>
              <span style={{ width: 32, height: 32, borderRadius: 'var(--r-ctrl)', background: rienEcrit ? 'var(--amber-bg)' : 'var(--teal-100)', color: rienEcrit ? 'var(--amber-fg)' : 'var(--teal-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon name="check" size={17} stroke={2.6} />
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)' }}>{rienEcrit ? t('Import terminé — rien n’a été écrit') : t('Import terminé')}</span>
                <span style={{ display: 'block', fontSize: 'var(--t-body)', color: 'var(--ink-600)', lineHeight: 1.5, marginTop: 3 }}>
                  <Trans
                    k='<1>{n}</1> article et <2>{decl}</2> créés.'
                    vars={{ n: done.createdModels, decl: t('{n} déclinaison', { n: done.created }) }}
                    parts={[(x) => <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{x}</strong>, (x) => <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{x}</strong>]}
                  />
                  {done.updated > 0 && <> {t('{n} fiche mise à jour.', { n: done.updated })}</>}
                </span>
                {ecartees > 0 && (
                  <span style={{ display: 'block', fontSize: 'var(--t-dense)', color: 'var(--amber-fg)', fontWeight: 600, marginTop: 4 }}>{t('{n} ligne écartée — voir le rapport.', { n: ecartees })}</span>
                )}
              </span>
              <FermerNotification />
            </div>
            <div style={{ display: 'flex', gap: 8, padding: '12px 18px', background: 'var(--ink-50)', borderTop: '1px solid var(--ink-200)' }}>
              <button type="button" onClick={() => setRapport(true)} style={{
                height: 36, padding: '0 16px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)',
                color: 'var(--ink-700)', fontSize: 'var(--t-body)', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer',
              }}>{t('Rapport')}</button>
              <div style={{ flex: 1 }} />
              {/* ⚠️ FERMER PUIS MONTRER : l'import part de l'écran des articles, donc y naviguer seul ne changeait rien à l'écran
                  — le bouton semblait mort. La notification se ferme, et la liste revient en tête (page 1, tri par défaut). */}
              <button type="button" onClick={() => { requestShowImported(); if (pathname !== '/articles') navigate('/articles'); }} style={{
                height: 36, padding: '0 16px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--teal-500)',
                color: 'var(--paper)', fontSize: 'var(--t-body)', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer',
              }}>{t('Voir les articles')}</button>
            </div>
          </div>
        )}
      </div>
      {done !== null && (
        <RapportImport open={rapport} onClose={() => setRapport(false)} report={done} fileName={job.fileName} screenRejected={job.screenRejected} />
      )}
    </>
  );
}

/** La croix de la notification — un import en vol ne se ferme pas, il se suit. */
function FermerNotification() {
  return (
    <button type="button" aria-label={t('Fermer la notification')} onClick={dismissImportJob} style={{
      width: 26, height: 26, border: 'none', borderRadius: 'var(--r-xs)', background: 'transparent', color: 'var(--ink-400)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: 0, cursor: 'pointer',
    }}>
      <Icon name="x" size={14} stroke={2} />
    </button>
  );
}

/**
 * LE RAPPORT COMPLET — ce que le serveur a RÉELLEMENT écrit : comptes, document, codes-barres internes attribués (à
 * étiqueter), lignes refusées avec le geste qui débloque.
 */
export function RapportImport({ open, onClose, report, fileName, screenRejected = [] }: {
  open: boolean; onClose: () => void; report: ImportArticlesReport; fileName: string; screenRejected?: readonly ImportScreenRejected[];
}) {
  const dest = report.destination;
  return (
    <Modal
      open={open} onClose={onClose} width="xl" title={t('Rapport d’import')} subtitle={fileName} icon="upload" tone="blue"
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={() => telecharger(report, fileName, screenRejected)}>{t('Télécharger le rapport')}</button>
          <div style={{ flex: 1 }} />
          <button type="button" className="btn-cta" onClick={onClose}>{t('Fermer')}</button>
        </>
      }
    >
      <div data-testid="import-rapport" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14 }}>
        <Tuile titre={t('Créés')} tone={report.created ? 'teal' : undefined} count={(l) => <Trans k='<1>{n}</1> <2>déclinaison nouvelle</2>' vars={{ n: report.created }} parts={l} />} />
        <Tuile titre={t('Mis à jour')} count={(l) => <Trans k='<1>{n}</1> <2>fiche complétée</2>' vars={{ n: report.updated }} parts={l} />} />
        <Tuile titre={t('Déjà en base')} count={(l) => <Trans k='<1>{n}</1> <2>laissé tel quel</2>' vars={{ n: report.skippedExisting }} parts={l} />} />
        <Tuile titre={t('Refusés')} count={(l) => <Trans k='<1>{n}</1> <2>ligne non écrite</2>' vars={{ n: report.refused.length }} parts={l} />} />
      </div>
      {report.replayed && (
        <p style={{ margin: '0 0 12px', fontSize: 'var(--t-body)', color: 'var(--ink-600)' }}>{t('Cet envoi avait déjà été traité : voici le compte rendu de la première fois, rien n’a été réécrit.')}</p>
      )}
      <div style={{ display: 'grid', gap: 8, marginBottom: 12, fontSize: 'var(--t-body)', color: 'var(--ink-700)', lineHeight: 1.55 }}>
        {dest !== null && (
          <div>{dest.number
            ? t('{dest} {number} : {n} ligne, {pieces}.', { dest: t(DESTINATION_LABEL[dest.kind]), number: dest.number, n: dest.lines, pieces: t('{n} pièce', { n: dest.pieces }) })
            : t('{dest} : {n} ligne, {pieces}.', { dest: t(DESTINATION_LABEL[dest.kind]), n: dest.lines, pieces: t('{n} pièce', { n: dest.pieces }) })}</div>
        )}
        {report.quantitiesIgnored > 0 && <div>{t('{n} ligne portait une quantité : « Articles seulement » ne l’a pas écrite.', { n: report.quantitiesIgnored })}</div>}
        {report.matchedWithoutBarcode > 0 && <div>{t('{n} ligne sans code-barres rapprochée de son article par le code article, la couleur et la taille.', { n: report.matchedWithoutBarcode })}</div>}
        {report.generatedCodes > 0 && <div>{t('{n} article créé avec un code article tiré de sa désignation.', { n: report.generatedCodes })}</div>}
        {report.familiesLearned > 0 && <div>{t('{n} correspondance de famille mémorisée pour ce fournisseur.', { n: report.familiesLearned })}</div>}
        {report.sizesAdded.map((s) => <div key={s.grid}>{t('Grille « {grid} » complétée : {valeurs}.', { grid: s.grid, valeurs: s.values.join(', ') })}</div>)}
        {report.secondAxisCreated.map((g) => <div key={g}>{t('Grille de 2ᵉ dimension créée et rattachée : « {g} ».', { g })}</div>)}
        {report.restoredModels > 0 && <div>{t('{n} modèle archivé remis en service par ce fichier : il redescend en caisse avec son stock et ses photos.', { n: report.restoredModels })}</div>}
      </div>
      {report.generatedGencodes.length > 0 && (
        <div style={{ border: '1px solid var(--teal-300)', borderRadius: 'var(--r-sm)', overflow: 'hidden', marginBottom: 12 }}>
          <div style={{ padding: '10px 14px', background: 'var(--teal-100)', borderBottom: '1px solid var(--teal-300)', fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--teal-800)' }}>
            {t('Codes-barres internes attribués — à étiqueter')}
          </div>
          {report.generatedGencodes.slice(0, 100).map((g) => (
            <div key={g.gencode} style={{ display: 'grid', gridTemplateColumns: '56px 130px minmax(0,1fr)', gap: 12, padding: '9px 14px', borderBottom: '1px solid var(--ink-100)', fontSize: 'var(--t-body)', alignItems: 'center' }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-400)', fontVariantNumeric: 'tabular-nums' }}>L{g.line}</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-800)' }}>{g.gencode}</div>
              <div style={{ color: 'var(--ink-700)', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.name}</div>
            </div>
          ))}
          {report.generatedGencodes.length > 100 && (
            <div style={{ padding: '10px 14px', background: 'var(--ink-50)', fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{t('… et {n} autres — le rapport téléchargé les porte tous.', { n: report.generatedGencodes.length - 100 })}</div>
          )}
        </div>
      )}
      {screenRejected.length > 0 && (
        <div data-testid="import-ecartees-ecran" style={{ border: '1px solid var(--amber)', borderRadius: 'var(--r-sm)', overflow: 'hidden', marginBottom: 12 }}>
          <div style={{ padding: '10px 14px', background: 'var(--amber-bg)', borderBottom: '1px solid var(--amber)', fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-800)' }}>{t('Lignes écartées avant l’envoi — elles ne sont pas parties')}</div>
          {screenRejected.slice(0, 100).map((r, i) => (
            <div key={`${r.line}-${i}`} style={{ display: 'grid', gridTemplateColumns: '56px minmax(0,1fr) minmax(0,1fr)', gap: 12, padding: '9px 14px', borderBottom: '1px solid var(--ink-100)', fontSize: 'var(--t-body)', alignItems: 'center' }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-400)', fontVariantNumeric: 'tabular-nums' }}>L{r.line}</div>
              <div style={{ color: 'var(--ink-800)', minWidth: 0 }}>{r.why}</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-600)', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.ex}</div>
            </div>
          ))}
          {screenRejected.length > 100 && (
            <div style={{ padding: '10px 14px', background: 'var(--ink-50)', fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{t('… et {n} autres lignes écartées — le rapport téléchargé les porte toutes.', { n: screenRejected.length - 100 })}</div>
          )}
        </div>
      )}
      {report.refused.length > 0 && (
        <div style={{ border: '1px solid var(--amber)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', background: 'var(--amber-bg)', borderBottom: '1px solid var(--amber)', fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-800)' }}>{t('Lignes refusées — le motif dit le geste qui débloque')}</div>
          {report.refused.slice(0, 100).map((r, i) => (
            <div key={`${r.line}-${i}`} style={{ display: 'grid', gridTemplateColumns: '56px 130px minmax(0,1fr)', gap: 12, padding: '9px 14px', borderBottom: '1px solid var(--ink-100)', fontSize: 'var(--t-body)', alignItems: 'center' }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-400)', fontVariantNumeric: 'tabular-nums' }}>L{r.line}</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-600)' }}>{r.gencode ?? '—'}</div>
              <div style={{ color: 'var(--ink-800)', minWidth: 0 }}>{r.reason}</div>
            </div>
          ))}
          {report.refused.length > 100 && (
            <div style={{ padding: '10px 14px', background: 'var(--ink-50)', fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{t('… et {n} autres lignes refusées — le rapport téléchargé les porte toutes.', { n: report.refused.length - 100 })}</div>
          )}
        </div>
      )}
    </Modal>
  );
}
